import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import { ISidebarAdminItem, ISidebarItem } from 'src/app/models';
import { CommunicationUserType, UserRole } from 'src/app/models/enums';
import { HomeRouteService, StorageService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { environment } from 'src/environments/environment';
import { images } from 'src/images';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() setRouteName = new EventEmitter<string>(true);
  readonly routeName = input('');

  public images = images;
  readonly role = signal(UserRole.ADMIN);
  public pendingSuggestionsCount = 0;
  readonly usersListType = signal(CommunicationUserType.USERS);
  readonly suggestionsListType = signal(CommunicationUserType.USERS);

  readonly submenuOpen = signal(false);
  readonly submenuShown = signal(false);
  readonly submenuHovered = signal(false);
  readonly adminSubmenuOpen = signal(false);
  readonly adminSubmenuHovered = signal(false);

  readonly isSuperAdmin = signal(false);
  readonly superAdminZoneActive = computed(() =>
    this.adminItems.some((item) => item.routeName === this.routeName()),
  );
  readonly adminItems: ISidebarAdminItem[] = [
    {
      icon: images.sidebar.clients,
      alt: 'clients',
      label: 'sidebar.clients',
      width: 18,
      height: 18,
      routerLink: '/clients',
      routeName: 'clients',
    },
    {
      icon: images.link,
      alt: 'integrations',
      label: 'sidebar.integrations',
      width: 20,
      height: 20,
      routerLink: '/integrations',
      routeName: 'integrations',
    },
  ];

  private adminItemHovered = false;
  private openTimeout?: number;
  private hideTimeout?: number;
  private subscriptions: Subscription[] = [];
  private requestedItem?: ISidebarItem;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private suggestionService: SuggestionService,
    private homeRouteService: HomeRouteService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) this.selectRequestedItem(event.urlAfterRedirects);
        if (event instanceof NavigationError) this.requestedItem = undefined;
        if (event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect) {
          this.requestedItem = undefined;
        }
      }),
    );
    this.subscriptions.push(
      this.suggestionService.pendingCount$.subscribe((count) => {
        this.pendingSuggestionsCount = count;
      }),
    );

    this.role.set(await this.storageService.getRole());
    this.isSuperAdmin.set(this.role() === UserRole.SUPERADMIN);
    await this.refreshListTypes();
    this.subscriptions.push(
      this.storageService.onRouteNameChange$.subscribe(() =>
        this.refreshListTypes(),
      ),
    );
  }

  readonly items = computed<ISidebarItem[]>(() => {
    const routeName = this.routeName();
    const role = this.role();
    const items: ISidebarItem[] = [
      {
        id: 'messagesSection',
        icon: images.sidebar.messages,
        alt: 'messages',
        label: 'sidebar.communications',
        routerLink: `/suggestions/${this.suggestionsListType()}`,
        routeName: `suggestions/${this.suggestionsListType()}`,
        isActive: (routeName) => routeName.includes('suggestions') || routeName.includes('messages'),
        badge: true,
        roles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OPERATOR, UserRole.DRIVER_MANAGER],
      },
      {
        id: 'usersSection',
        icon: images.sidebar.users,
        alt: 'users',
        label: 'sidebar.users',
        routerLink: `/${this.usersListType()}`,
        routeName: this.usersListType(),
        isActive: (routeName) => routeName === 'users' || routeName === 'drivers',
      },
      {
        id: 'bookingsSection',
        icon: images.sidebar.bookings,
        alt: 'bookings',
        label: 'sidebar.bookings',
        routerLink: '/bookings',
        routeName: 'bookings',
        isActive: (routeName) => routeName === 'bookings',
      },
      {
        id: 'tripsSection',
        icon: images.sidebar.trips,
        alt: 'trips',
        label: 'sidebar.trips',
        routerLink: '/trips',
        routeName: 'trips',
        isActive: (routeName) => routeName === 'trips',
      },
      // {
      //   id: 'operatorsSection',
      //   icon: images.sidebar.operators,
      //   alt: 'operators',
      //   label: 'sidebar.operators',
      //   href: `${backofficeUrl}operators`,
      // },
      {
        id: 'servicesSection',
        icon: images.sidebar.services,
        alt: 'services',
        label: 'sidebar.services',
        routerLink: '/services',
        routeName: 'services',
        isActive: (routeName) => routeName === 'services',
        roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
      },
      // {
      //   id: 'townsSection',
      //   icon: images.sidebar.towns,
      //   alt: 'towns',
      //   label: 'sidebar.towns',
      //   href: `${backofficeUrl}towns`,
      // },
      {
        id: 'vehiclesSection',
        icon: images.sidebar.vehicles,
        alt: 'vehicles',
        label: 'sidebar.vehicles',
        routerLink: APP.VEHICLES_PATH,
        routeName: 'vehicles',
        isActive: (routeName) => routeName === 'vehicles',
        roles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.DRIVER_MANAGER],
      },
      {
        id: 'dashboardSection',
        icon: images.sidebar.dashboard,
        alt: 'dashboard',
        label: 'sidebar.dashboard',
        href: this.getDashboardUrl(),
        roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
        bottom: true,
      },
    ];
    return items
      .filter((item) => !item.roles || item.roles.includes(role))
      .map((item) => ({ ...item, active: !!item.isActive?.(routeName) }));
  });

  readonly topItems = computed(() => this.items().filter((item) => !item.bottom));
  readonly bottomItems = computed(() => this.items().filter((item) => item.bottom));

  get pendingBadge(): string {
    return this.pendingSuggestionsCount > 99 ? '99+' : `${this.pendingSuggestionsCount}`;
  }

  isActive(item: ISidebarItem): boolean {
    return !!item.isActive?.(this.routeName());
  }

  onItemClick(item: ISidebarItem) {
    if (item.routeName) this.requestedItem = item;
  }

  private selectRequestedItem(url: string) {
    const requestedItem = this.requestedItem;
    this.requestedItem = undefined;
    if (!requestedItem?.routeName || !requestedItem.routerLink) return;
    if (this.utilsService.isUrlUnderPath(url, requestedItem.routerLink)) {
      this.setRouteNameSelected(requestedItem.routeName);
    }
  }

  onAdminItemClick(item: ISidebarAdminItem) {
    this.setRouteNameSelected(item.routeName);
    this.onAdminSubmenuLeave();
  }

  public setRouteNameSelected(routeName: string) {
    this.setRouteName.emit(routeName);
  }

  onItemEnter() {
    if (this.openTimeout !== undefined || this.utilsService.isTouchDevice()) return;
    this.openTimeout = window.setTimeout(() => {
      this.openTimeout = undefined;
      this.openSubmenu();
    }, APP.SIDEBAR_OPEN_DELAY_MS);
  }

  onItemLeave() {
    this.cancelOpen();
  }

  onAdminItemEnter() {
    this.adminItemHovered = true;
    this.onItemEnter();
  }

  expandSuperAdminZone() {
    this.cancelOpen();
    this.openSubmenu();
    this.adminSubmenuOpen.set(true);
  }

  onAdminItemLeave() {
    this.adminItemHovered = false;
    this.onItemLeave();
  }

  onSubmenuEnter() {
    this.submenuHovered.set(true);
    this.openSubmenu();
  }

  onSubmenuLeave() {
    this.submenuHovered.set(false);
    if (!this.adminItemHovered && !this.adminSubmenuHovered()) this.closeSubmenu();
  }

  onAdminSubmenuItemEnter() {
    this.openSubmenu();
    this.adminSubmenuOpen.set(true);
  }

  onAdminSubmenuItemLeave() {
    this.adminSubmenuOpen.set(false);
  }

  onAdminSubmenuEnter() {
    this.adminSubmenuHovered.set(true);
    this.onAdminSubmenuItemEnter();
  }

  onAdminSubmenuLeave() {
    this.adminSubmenuHovered.set(false);
    this.adminSubmenuOpen.set(false);
    this.closeSubmenu();
  }

  @HostListener('document:mouseleave')
  onDocumentLeave() {
    this.cancelOpen();
    window.clearTimeout(this.hideTimeout);
    this.submenuOpen.set(false);
    this.submenuShown.set(false);
    this.adminSubmenuOpen.set(false);
  }

  private openSubmenu() {
    window.clearTimeout(this.hideTimeout);
    this.submenuOpen.set(true);
    this.submenuShown.set(true);
  }

  private closeSubmenu() {
    this.cancelOpen();
    this.submenuOpen.set(false);
    this.adminSubmenuOpen.set(false);
    window.clearTimeout(this.hideTimeout);
    this.hideTimeout = window.setTimeout(() => this.submenuShown.set(false), APP.SIDEBAR_CLOSE_DELAY_MS);
  }

  private cancelOpen() {
    window.clearTimeout(this.openTimeout);
    this.openTimeout = undefined;
  }

  private async refreshListTypes() {
    const [usersListType, suggestionsListType] = await Promise.all([
      this.homeRouteService.getUsersListType(this.role()),
      this.homeRouteService.getSuggestionsListType(this.role()),
    ]);
    this.usersListType.set(usersListType);
    this.suggestionsListType.set(suggestionsListType);
  }

  getDashboardUrl() {
    if (environment.location === 'tst') {
      return 'https://dashboard-tst.nemi.mobi/';
    } else if (environment.location === 'demo') {
      return 'https://dashboard-dmo.nemi.mobi/';
    } else if (environment.location === 'sta') {
      return 'https://dashboard-sta.nemi.mobi/';
    } else if (environment.location === 'prod') {
      return 'https://dashboard.nemi.mobi/';
    }
    return 'https://dashboard.nemi.mobi/';
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.cancelOpen();
    window.clearTimeout(this.hideTimeout);
  }
}
